import { useState, type FormEvent } from 'react'
import { Sparkles, Loader2 } from 'lucide-react'
import { api } from '@/lib/api'
import { captureEvent } from '@/lib/analytics'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'

// In-app "summarize any YouTube link" — the counterpart to the public
// Rundown-any-link feature. Fire-and-forget: it queues the summary into our DB
// (marked ad-hoc); it does NOT follow the channel or add to the Library. We
// surface a short confirmation and clear the field; there's no result view.
type Status =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'done'; message: string }
  | { kind: 'error'; message: string }

export function RundownInput() {
  const [url, setUrl] = useState('')
  const [status, setStatus] = useState<Status>({ kind: 'idle' })

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    const value = url.trim()
    if (!value || status.kind === 'loading') return
    setStatus({ kind: 'loading' })
    try {
      const res = await api.rundown(value)
      captureEvent('rundown_submitted', { status: res.status })
      setUrl('')
      setStatus({
        kind: 'done',
        message: res.status === 'ready' ? 'Already summarized.' : 'Summarizing — it’ll be ready shortly.',
      })
    } catch (err) {
      setStatus({ kind: 'error', message: err instanceof Error ? err.message : 'Something went wrong.' })
    }
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-1.5">
      <div className="flex gap-2">
        <Input
          type="url"
          inputMode="url"
          placeholder="Paste a YouTube link to summarize…"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value)
            if (status.kind !== 'idle' && status.kind !== 'loading') setStatus({ kind: 'idle' })
          }}
          aria-label="YouTube video URL to summarize"
        />
        <Button type="submit" disabled={!url.trim() || status.kind === 'loading'}>
          {status.kind === 'loading' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          <span className="ml-2 hidden sm:inline">Summarize</span>
        </Button>
      </div>
      {(status.kind === 'done' || status.kind === 'error') && (
        <p className={status.kind === 'error' ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'}>
          {status.message}
        </p>
      )}
    </form>
  )
}
